import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  ChevronLeftIcon,
  Cog6ToothIcon,
  KeyIcon,
  MagnifyingGlassCircleIcon,
} from '@heroicons/react/24/outline'
import { Button } from '../../components/ui/button'
import { Card, CardContent } from '../../components/ui/card'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { useAuth } from '../../contexts/AuthContext'
import HelpScreenButton from '../../components/help/HelpScreenButton'
import ScreenHelpPanel from '../../components/help/ScreenHelpPanel'
import ScreeningKeysPanel from './ScreeningKeysPanel'

const SETTINGS_HELP_SCREEN_KEY = 'page:compliance-safe/settings/patron-screening'

const TABS = [
  { id: 'catalog', label: 'Sources & purposes', icon: MagnifyingGlassCircleIcon },
  { id: 'keys', label: 'API keys', icon: KeyIcon },
]

function groupByCategory(rows) {
  return rows.reduce((acc, row) => {
    const cat = row.category || 'Other'
    if (!acc[cat]) acc[cat] = []
    acc[cat].push(row)
    return acc
  }, {})
}

function CatalogSection({ title, rows, canManage, onToggle, onToggleAll }) {
  const allOn = rows.every((r) => r.isEnabled)
  const allOff = rows.every((r) => !r.isEnabled)
  return (
    <div className="mb-6">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h3 className="text-sm font-semibold text-psg-teal m-0">{title}</h3>
        {canManage && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onToggleAll(rows.map((r) => r.id), !allOn)}
          >
            {allOff ? 'Enable all' : allOn ? 'Disable all' : 'Enable all'}
          </Button>
        )}
      </div>
      <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
        {rows.map((row) => (
          <label
            key={row.id}
            className={`flex items-center justify-between gap-3 px-4 py-3 bg-white ${canManage ? 'cursor-pointer hover:bg-gray-50' : ''}`}
          >
            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-900">{row.name || row.label}</div>
              {row.desc && <div className="text-xs text-gray-500 mt-0.5">{row.desc}</div>}
              {row.disabledByDefault && (
                <div className="text-xs text-amber-700 mt-0.5">Off by default (upstream often blocked)</div>
              )}
              {row.liveSourceId && (
                <div className="text-xs text-gray-400 mt-0.5">Live runner: {row.liveSourceId}</div>
              )}
            </div>
            <input
              type="checkbox"
              className="rounded border-gray-300 text-psg-teal focus:ring-psg-teal shrink-0"
              checked={!!row.isEnabled}
              disabled={!canManage}
              onChange={() => onToggle(row.id)}
            />
          </label>
        ))}
      </div>
    </div>
  )
}

export default function PatronScreeningSettings() {
  const { can } = useAuth()
  const canManage = can('compliance-safe/patron-screening', 'manage')
  const canView = can('compliance-safe/patron-screening', 'view')
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = searchParams.get('tab') === 'keys' ? 'keys' : 'catalog'

  const [sources, setSources] = useState([])
  const [purposes, setPurposes] = useState([])
  const [toolEnabled, setToolEnabled] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)
  const [msg, setMsg] = useState(null)
  const [helpPanelOpen, setHelpPanelOpen] = useState(false)
  const [dirty, setDirty] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setErr(null)
    try {
      const res = await authFetch(getLocalApiUrl('/api/patron-screening/settings'))
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`)
      setSources(json.sources || [])
      setPurposes(json.purposes || [])
      setToolEnabled(json.toolEnabled === true)
      setDirty(false)
    } catch (e) {
      setErr(e.message || 'Failed to load settings')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { if (canView) load() }, [load, canView])

  const sourcesByCategory = useMemo(() => groupByCategory(sources), [sources])

  const setTab = (id) => {
    const next = new URLSearchParams(searchParams)
    if (id === 'catalog') next.delete('tab')
    else next.set('tab', id)
    setSearchParams(next, { replace: true })
  }

  const toggleSource = (id) => {
    setSources((prev) => prev.map((s) => (s.id === id ? { ...s, isEnabled: !s.isEnabled } : s)))
    setDirty(true)
    setMsg(null)
  }

  const togglePurpose = (id) => {
    setPurposes((prev) => prev.map((p) => (p.id === id ? { ...p, isEnabled: !p.isEnabled } : p)))
    setDirty(true)
    setMsg(null)
  }

  const toggleAllSources = (ids, enabled) => {
    const idSet = new Set(ids)
    setSources((prev) => prev.map((s) => (idSet.has(s.id) ? { ...s, isEnabled: enabled } : s)))
    setDirty(true)
    setMsg(null)
  }

  const saveCatalog = async () => {
    if (!canManage) return
    setSaving(true)
    setErr(null)
    setMsg(null)
    try {
      const res = await authFetch(getLocalApiUrl('/api/patron-screening/settings'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sources: sources.map((s) => ({ id: s.id, isEnabled: s.isEnabled })),
          purposes: purposes.map((p) => ({ id: p.id, isEnabled: p.isEnabled })),
          toolEnabled,
        }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`)
      setSources(json.sources || [])
      setPurposes(json.purposes || [])
      setToolEnabled(json.toolEnabled === true)
      setDirty(false)
      setMsg('Settings saved.')
    } catch (e) {
      setErr(e.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  if (!canView) {
    return (
      <div className="py-16 text-center text-gray-600">
        You need <code className="text-sm bg-gray-100 px-1 rounded">compliance-safe/patron-screening:view</code> to access patron screening settings.
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link to="/compliance-safe/ecdd" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-600">
          <ChevronLeftIcon className="w-4 h-4" /> Back to ECDD
        </Link>
        <HelpScreenButton screenKey={SETTINGS_HELP_SCREEN_KEY} onClick={() => setHelpPanelOpen(true)} />
      </div>

      <Card>
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-1">
            <Cog6ToothIcon className="w-5 h-5 text-psg-teal" />
            <h1 className="text-xl font-bold text-psg-teal m-0">Patron Screening Settings</h1>
          </div>
          <p className="text-sm text-gray-500 m-0">
            Control the ECDD screening tool entry point, which OSINT sources appear in scans, and which scan purposes analysts can choose.
          </p>
        </CardContent>
      </Card>

      <div className="flex gap-2 border-b border-gray-200">
        {TABS.map((t) => {
          const Icon = t.icon
          const active = tab === t.id
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                active ? 'border-psg-teal text-psg-teal' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          )
        })}
      </div>

      {tab === 'keys' ? (
        <ScreeningKeysPanel canManage={canManage} />
      ) : (
        <Card>
          <CardContent className="p-5">
            {loading ? (
              <div className="py-8 text-center text-gray-500 text-sm">Loading…</div>
            ) : err ? (
              <div className="py-8 text-center text-red-600 text-sm">{err}</div>
            ) : (
              <>
                <div className="mb-6 pb-6 border-b border-gray-100">
                  <h2 className="text-base font-semibold text-gray-800 mb-1">ECDD screening tool</h2>
                  <p className="text-sm text-gray-500 mb-3">
                    When enabled, analysts with patron screening access see the <strong>Screening sources</strong> button on ECDD list and detail screens. Off by default for new tenants.
                  </p>
                  <label
                    className={`flex items-center justify-between gap-3 px-4 py-3 border border-gray-200 rounded-lg bg-white ${canManage ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                  >
                    <div>
                      <div className="text-sm font-medium text-gray-900">Show Screening sources on ECDD</div>
                      <div className="text-xs text-gray-500 mt-0.5">Hides the OSINT tool button for this tenant without changing source or purpose catalog.</div>
                    </div>
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-psg-teal focus:ring-psg-teal shrink-0"
                      checked={!!toolEnabled}
                      disabled={!canManage}
                      onChange={() => {
                        setToolEnabled((v) => !v)
                        setDirty(true)
                        setMsg(null)
                      }}
                    />
                  </label>
                </div>

                <div className="mb-6">
                  <h2 className="text-base font-semibold text-gray-800 mb-1">Scan purposes</h2>
                  <p className="text-sm text-gray-500 mb-3">
                    Disable <strong>Full screening</strong> to hide the all-sources option from analysts. Other purposes limit which sources are eligible during a scan.
                  </p>
                  <CatalogSection
                    title="Investigation purposes"
                    rows={purposes}
                    canManage={canManage}
                    onToggle={togglePurpose}
                    onToggleAll={(ids, enabled) => {
                      const idSet = new Set(ids)
                      setPurposes((prev) => prev.map((p) => (idSet.has(p.id) ? { ...p, isEnabled: enabled } : p)))
                      setDirty(true)
                    }}
                  />
                </div>

                <div>
                  <h2 className="text-base font-semibold text-gray-800 mb-1">OSINT sources</h2>
                  <p className="text-sm text-gray-500 mb-3">
                    Disabled sources are hidden from the screening tool for this tenant. Live integrations still require API keys where applicable.
                  </p>
                  {Object.entries(sourcesByCategory).map(([cat, rows]) => (
                    <CatalogSection
                      key={cat}
                      title={cat}
                      rows={rows}
                      canManage={canManage}
                      onToggle={toggleSource}
                      onToggleAll={toggleAllSources}
                    />
                  ))}
                </div>

                {canManage && (
                  <div className="flex items-center gap-3 mt-6 pt-4 border-t border-gray-100">
                    <Button type="button" onClick={saveCatalog} disabled={saving || !dirty}>
                      {saving ? 'Saving…' : 'Save catalog'}
                    </Button>
                    {msg && <span className="text-sm text-green-700">{msg}</span>}
                    {dirty && !msg && <span className="text-sm text-gray-500">Unsaved changes</span>}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      <ScreenHelpPanel
        screenKey={SETTINGS_HELP_SCREEN_KEY}
        open={helpPanelOpen}
        onClose={() => setHelpPanelOpen(false)}
        sectionIdPrefix="patron-screening-settings-help"
      />
    </div>
  )
}
